"use client";

import * as React from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import {
  Clapperboard,
  Download,
  FileText,
  Info,
  MoreHorizontal,
  Music2,
  Pause,
  Play,
} from "lucide-react";
import {
  AsyncBoundary,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  EmptyState,
} from "@gerama/ui";
import { usePlayerStore } from "@gerama/stores";
import { cn, formatDuration, formatJalaliDate, formatNumber } from "@gerama/utils";
import type { SongDetail as SongDetailType } from "@gerama/types";
import { MediaImage as Image } from "@/components/media-image";
import { artistPath } from "@/features/artists/artist-path";
import { publicPath } from "@/lib/public-path";
import { browserCoreApi } from "@/lib/api";
import { VideoCard } from "@/features/discover/video-card";
import { LikeButton } from "@/features/player/like-button";
import { songToTrack } from "@/features/player/to-track";
import { useLyrics } from "@/features/player/lyrics-view";
import { FollowArtistButton } from "@/features/artists/follow-artist-button";
import { useSong } from "./use-song";
import { SongDetailSkeleton } from "./song-detail-skeleton";
import { RelatedSongs } from "./related-songs";
import { SongContextMenu } from "./song-context-menu";
import { ROLE_LABEL, useSongActions } from "./use-song-actions";

export function SongDetail({ id }: { id: number | string }) {
  const query = useSong(id);

  return (
    <AsyncBoundary
      variant="immersive"
      query={query}
      skeleton={<SongDetailSkeleton />}
      isEmpty={() => false}
      empty={
        <EmptyState
          variant="immersive"
          icon={<Music2 className="size-8" />}
          title="آهنگ یافت نشد"
        />
      }
      render={(song) => <SongView song={song} />}
    />
  );
}

const iconButton =
  "text-muted-foreground hover:text-foreground border-border/60 hover:border-foreground/50 flex size-11 items-center justify-center rounded-full border transition-colors";

function SongView({ song }: { song: SongDetailType }) {
  const actions = useSongActions(song);
  const isCurrent = usePlayerStore(
    (s) => s.current()?.kind === "song" && s.current()?.id === song.id,
  );
  const isPlaying = usePlayerStore((s) => s.isPlaying);
  const [infoOpen, setInfoOpen] = React.useState(false);
  const lyrics = useLyrics(song.id);
  const lyricsRef = React.useRef<HTMLElement>(null);
  const hasLyrics = (lyrics.data?.length ?? 0) > 0;

  const play = () => {
    const store = usePlayerStore.getState();
    if (isCurrent) return store.togglePlay();
    store.playSingle(songToTrack(song), { label: song.name, href: publicPath("songs", song) });
  };
  const released = formatJalaliDate(song.releaseDate ?? song.createdAt);

  return (
    <article className="space-y-10">
      <header className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-end sm:text-start">
        <div className="bg-muted relative size-52 shrink-0 overflow-hidden rounded-lg shadow-[0_10px_40px_rgba(0,0,0,0.5)] sm:size-60">
          {song.cover ? (
            <Image
              src={song.cover}
              alt={song.name}
              fill
              priority
              sizes="240px"
              className="object-cover"
            />
          ) : (
            <Music2 className="text-muted-foreground absolute inset-0 m-auto size-14" />
          )}
        </div>
        <div className="min-w-0 space-y-3">
          <h1 className="text-3xl leading-tight font-black sm:text-5xl">{song.name}</h1>
          {song.artists.length > 0 && (
            <p className="text-xl sm:text-2xl">
              {song.artists.map((artist, i) => (
                <React.Fragment key={artist.id}>
                  {i > 0 && "، "}
                  <Link href={artistPath(artist)} className="hover:underline">
                    {artist.name}
                  </Link>
                </React.Fragment>
              ))}
            </p>
          )}
          <p className="text-muted-foreground text-sm">
            آهنگ<span className="mx-2">·</span>
            <span dir="ltr" className="tabular-nums">
              {formatDuration(song.duration)}
            </span>
            <span className="mx-2">·</span>
            {released}
            <span className="mx-2">·</span>
            {formatNumber(song.playCount, "fa")} پخش
          </p>
          <div className="flex items-center justify-center gap-3 pt-1 sm:justify-start">
            <button
              type="button"
              onClick={play}
              aria-label={isCurrent && isPlaying ? "توقف" : "پخش"}
              className="solid-primary text-primary-foreground flex size-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95"
            >
              {isCurrent && isPlaying ? (
                <Pause className="size-6 fill-current" />
              ) : (
                <Play className="size-6 fill-current" />
              )}
            </button>
            <LikeButton
              itemType="song"
              itemId={song.id}
              variant="icon"
              className={cn(iconButton, "[&_svg]:size-5")}
            />
            <button
              type="button"
              onClick={actions.download}
              aria-label="دانلود"
              title="دانلود"
              className={iconButton}
            >
              <Download className="size-5" />
            </button>
            {hasLyrics && (
              <button
                type="button"
                onClick={() =>
                  lyricsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                aria-label="متن آهنگ"
                title="متن آهنگ"
                className={iconButton}
              >
                <FileText className="size-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              aria-label="اطلاعات"
              title="اطلاعات"
              className={iconButton}
            >
              <Info className="size-5" />
            </button>
            <SongContextMenu song={song}>
              <button type="button" aria-label="گزینه‌های بیشتر" className={iconButton}>
                <MoreHorizontal className="size-5" />
              </button>
            </SongContextMenu>
          </div>
        </div>
      </header>

      {song.description && (
        <p className="text-muted-foreground max-w-3xl leading-7">{song.description}</p>
      )}

      {hasLyrics && <LyricsSection ref={lyricsRef} lines={lyrics.data!.map((l) => l.text)} />}

      {song.artists.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-bold">{song.artists.length > 1 ? "هنرمندان" : "هنرمند"}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {song.artists.map((artist) => (
              <div key={artist.id} className="bg-card flex items-center gap-4 rounded-xl p-3">
                <Link
                  href={artistPath(artist)}
                  className="bg-muted relative size-16 shrink-0 overflow-hidden rounded-full"
                  aria-hidden
                  tabIndex={-1}
                >
                  {artist.cover && (
                    <Image src={artist.cover} alt="" fill sizes="64px" className="object-cover" />
                  )}
                </Link>
                <Link href={artistPath(artist)} className="min-w-0 flex-1">
                  <p className="truncate font-bold hover:underline">{artist.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {ROLE_LABEL[artist.role] ?? "هنرمند"}
                  </p>
                </Link>
                <FollowArtistButton artistId={artist.id} variant="star" />
              </div>
            ))}
          </div>
        </section>
      )}

      {song.album && (
        <section className="space-y-3">
          <h2 className="text-xl font-bold">آلبوم</h2>
          <Link
            href={publicPath("albums", song.album)}
            className="bg-card hover:bg-accent/60 flex items-center gap-4 rounded-xl p-3 transition-colors"
          >
            <span className="bg-muted relative size-16 shrink-0 overflow-hidden rounded-md">
              {song.album.cover && (
                <Image src={song.album.cover} alt="" fill sizes="64px" className="object-cover" />
              )}
            </span>
            <span className="font-semibold">{song.album.name}</span>
          </Link>
        </section>
      )}

      <SongClip songId={song.id} songCode={song.code} songName={song.name} />

      <RelatedSongs id={song.code || song.id} code={song.code} />

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{song.name}</DialogTitle>
          </DialogHeader>
          <dl className="space-y-3 text-sm">
            {song.artists.map((artist) => (
              <div key={artist.id} className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{ROLE_LABEL[artist.role] ?? "هنرمند"}</dt>
                <dd>{artist.name}</dd>
              </div>
            ))}
            {song.album && (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">آلبوم</dt>
                <dd>{song.album.name}</dd>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">تاریخ انتشار</dt>
              <dd>{released}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">مدت</dt>
              <dd dir="ltr">{formatDuration(song.duration)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">تعداد پخش</dt>
              <dd>{formatNumber(song.playCount, "fa")}</dd>
            </div>
          </dl>
        </DialogContent>
      </Dialog>
    </article>
  );
}

const LYRICS_PREVIEW_LINES = 20;

const LyricsSection = React.forwardRef<HTMLElement, { lines: string[] }>(function LyricsSection(
  { lines },
  ref,
) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <section ref={ref} className="scroll-mt-24 space-y-3">
      <h2 className="text-xl font-bold">متن آهنگ</h2>
      <div className="relative max-w-2xl">
        {/* The full text is always in the DOM (crawlers index it); collapsing is only visual. */}
        <p
          className={cn(
            "text-lg leading-9 whitespace-pre-line",
            !expanded && lines.length > LYRICS_PREVIEW_LINES && "max-h-[45rem] overflow-hidden",
          )}
        >
          {lines.join("\n")}
        </p>
        {!expanded && lines.length > LYRICS_PREVIEW_LINES && (
          <div className="from-background pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t to-transparent" />
        )}
      </div>
      {lines.length > LYRICS_PREVIEW_LINES && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="border-border hover:border-foreground rounded-full border px-5 py-1.5 text-sm font-bold"
        >
          {expanded ? "نمایش کمتر" : "نمایش بیشتر"}
        </button>
      )}
    </section>
  );
});

/** The video ↔ song link, seen from the song: its clip(s), when it has any. */
function SongClip({ songId, songCode, songName }: { songId: number; songCode: string; songName: string }) {
  const clips = useQuery({
    queryKey: ["videos", "of-song", songId],
    queryFn: ({ signal }) => browserCoreApi.videos.list({ songId, limit: 3 }, signal),
    staleTime: 5 * 60_000,
  });
  const list = clips.data?.list ?? [];
  if (list.length === 0) return null;
  return (
    <section className="space-y-4">
      <h2 className="flex items-center gap-2 text-xl font-bold">
        <Clapperboard className="size-5" />
        نماهنگ
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            contextLabel={songName}
            contextHref={publicPath("songs", { id: songId, code: songCode })}
          />
        ))}
      </div>
    </section>
  );
}
